import React, { useEffect, useMemo, useState } from 'react';
import { Chip, Container, Grid, Typography, Card } from '@mui/material';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import DataFilter from './components/filter';
import { useSelector } from 'react-redux';
import moment from 'moment/moment';
import { LineChart } from '@mui/x-charts/LineChart';
import DataGridTable from '../../../../components/table/DataGridTable';
import { capitalize } from '../../../../utils/common';

const PurchasingDashboard = () => {
  const { themeStretch } = useSettings();
  const [originalData, setOriginalData] = useState([]);
  const [data, setData] = useState([]);
  const [reorderData, setReorderData] = useState([]);
  let selectedLocation;
  ({ selectedLocation } = useSelector((state) => state.location));

  const getSalesPattern = () => {
    const dates = [...new Set(data.map((item) => item.dateOnly))].sort((a, b) => moment(a).diff(moment(b)));
    const foodNames = [...new Set(data.map((item) => item.foodName))];
    const series = foodNames.map((foodName) => {
      const seriesData = dates.map((date) => {
        const sale = data.find((item) => item.dateOnly === date && item.foodName === foodName);
        return sale ? sale.costPerUnit : 0;
      });
      return { label: foodName, data: seriesData };
    });
    return { xAxis: dates.map((date) => moment(date).format('YYYY-MM-DD')), series };
  };

  const value = getSalesPattern();

  const customize = {
    height: 700,
    legend: { hidden: false },
    margin: { top: 20, bottom: 50, left: 50, right: 20 }
  };

  const columns = reorderData?.[0]
    ? Object.keys(reorderData?.[0])
      .map((value) => {
        if (['foodId', 'locationId'].includes(value)) {
          return null;
        } else if (value === 'status') {
          return {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ row }) => {
              return row?.original?.status && <Chip key={value} label={row?.original?.status} color={'error'} />;
            }
          };
        } else {
          return {
            accessorKey: value,
            header: capitalize(value)
          };
        }
      })
      .filter(Boolean)
    : [];

  const buildOrderSummary = (rows) => {
    const map = new Map();
    rows?.forEach((r) => {
      const required = Math.max(0, Number(r?.maxQty || 0) - Number(r?.totalStock || 0));
      const id = r.foodId;
      const sym = r?.symbol || '';
      const minQ = Number(r?.reorderQty ?? r?.minQty ?? 0);
      const maxQ = Number(r?.maxQty ?? 0);
      if (!map.has(id)) map.set(id, { foodId: id, foodName: r.foodName, symbol: sym, totalRequired: 0, minQty: Number.isFinite(minQ) ? minQ : 0, maxQty: Number.isFinite(maxQ) ? maxQ : 0 });
      const cur = map.get(id);
      const sum = Number(cur.totalRequired || 0) + Number(required || 0);
      cur.totalRequired = parseFloat(sum.toFixed(2));
      if (!cur.symbol && sym) cur.symbol = sym;
      if (Number.isFinite(minQ)) cur.minQty = cur.minQty === 0 ? minQ : Math.min(cur.minQty, minQ);
      if (Number.isFinite(maxQ)) cur.maxQty = Math.max(cur.maxQty || 0, maxQ);
      map.set(id, cur);
    });
    return Array.from(map.values())
      .filter((x) => Number(x.totalRequired) > 0)
      .sort((a, b) => Number(b.totalRequired) - Number(a.totalRequired));
  };

  const summaryData = useMemo(() => buildOrderSummary(reorderData), [reorderData]);

  const summaryColumns = [
    { accessorKey: 'foodName', header: 'Food Name' },
    {
      accessorKey: 'minQty',
      header: 'Min Qty',
      Cell: ({ row }) => {
        const n = Number(row?.original?.minQty || 0);
        const sym = row?.original?.symbol || '';
        return `${n.toFixed(2)} ${sym}`.trim();
      }
    },
    {
      accessorKey: 'maxQty',
      header: 'Max Qty',
      Cell: ({ row }) => {
        const n = Number(row?.original?.maxQty || 0);
        const sym = row?.original?.symbol || '';
        return `${n.toFixed(2)} ${sym}`.trim();
      }
    },
    {
      accessorKey: 'totalRequired',
      header: 'Total Required',
      Cell: ({ row }) => {
        const n = Number(row?.original?.totalRequired || 0);
        const sym = row?.original?.symbol || '';
        return `${n.toFixed(2)} ${sym}`.trim();
      }
    }
  ];

  return (
    <Page title="Purchasing Dashboard">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Grid container spacing={2}>
          <DataFilter data={data} setData={setData} setReorderData={setReorderData} setOriginalData={setOriginalData} />
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Purchasing Trend by Food (Cost Per Unit)
              </Typography>
              <LineChart xAxis={[{ scaleType: 'point', data: value.xAxis, label: 'Date' }]} yAxis={[{ label: 'Cost Per Unit' }]} series={value.series} {...customize} />
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Reorder List
              </Typography>
              <DataGridTable
                name={'Reorder List'}
                data={reorderData}
                column={columns}
                onRowClick={() => {}}
                isRowClickable={false}
                isLoading={false}
                setRowSelection={() => {}}
                enableRowSelection={false}
                enableRowActions={false}
                renderRowActionItems={() => []}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Total Required To Order (All Locations)
              </Typography>
              <DataGridTable
                name={'Total Required To Order'}
                data={summaryData}
                column={summaryColumns}
                onRowClick={() => {}}
                isRowClickable={false}
                isLoading={false}
                setRowSelection={() => {}}
                enableRowSelection={false}
                enableRowActions={false}
                renderRowActionItems={() => []}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default PurchasingDashboard;
